import React, { useState } from 'react';
import styled from 'styled-components';
import { FaLock } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { Input } from '../common/Input';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

const InfoBox = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1.5;
`;

const SecurityTab: React.FC = () => {
  const { 
    updatePassword, 
    error: authError, 
    loading: authLoading, 
    clearError: clearAuthError 
  } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ 
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const handleUpdatePassword = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs et le message de succès
    clearAuthError();
    setValidationErrors({});
    setUpdateSuccess(false);
    
    // Valider le formulaire
    const errors: { 
      currentPassword?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {};
    
    if (!currentPassword) {
      errors.currentPassword = 'Le mot de passe actuel est requis';
    }
    
    if (!newPassword) {
      errors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (newPassword.length < 6) {
      errors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'La confirmation du mot de passe est requise';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Mettre à jour le mot de passe
    updatePassword(newPassword)
      .then(() => {
        setUpdateSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Masquer le message de succès après 5 secondes
        setTimeout(() => {
          setUpdateSuccess(false);
        }, 5000);
      })
      .catch((error) => {
        console.error('Erreur lors de la mise à jour du mot de passe:', error);
      });
  };

  return (
    <Container>
      <SectionTitle>Sécurité et mot de passe</SectionTitle>
      
      <InfoBox>
        💡 <strong>Conseil de sécurité :</strong> Utilisez un mot de passe fort contenant au moins 8 caractères, 
        avec des lettres majuscules, minuscules, des chiffres et des caractères spéciaux.
      </InfoBox>
      
      <FormContainer onSubmit={handleUpdatePassword}>
        <FormGroup>
          <Label htmlFor="currentPassword">Mot de passe actuel</Label>
          <InputWrapper>
            <IconWrapper>
              <FaLock />
            </IconWrapper>
            <StyledInput
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Votre mot de passe actuel"
              $hasError={!!validationErrors.currentPassword}
            />
          </InputWrapper>
          {validationErrors.currentPassword && <ErrorMessage>{validationErrors.currentPassword}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="newPassword">Nouveau mot de passe</Label>
          <InputWrapper>
            <IconWrapper>
              <FaLock />
            </IconWrapper>
            <StyledInput
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Votre nouveau mot de passe"
              $hasError={!!validationErrors.newPassword}
            />
          </InputWrapper>
          {validationErrors.newPassword && <ErrorMessage>{validationErrors.newPassword}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
          <InputWrapper>
            <IconWrapper>
              <FaLock />
            </IconWrapper>
            <StyledInput
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmez votre nouveau mot de passe"
              $hasError={!!validationErrors.confirmPassword}
            />
          </InputWrapper>
          {validationErrors.confirmPassword && <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>}
        </FormGroup>
        
        {authError && <ErrorMessage>{authError.message}</ErrorMessage>}
        
        {updateSuccess && (
          <SuccessMessage>
            ✓ Votre mot de passe a été mis à jour avec succès !
          </SuccessMessage>
        )}
        
        <Button type="submit" $variant="primary" disabled={authLoading}>
          {authLoading ? 'Mise à jour en cours...' : 'Mettre à jour le mot de passe'}
        </Button>
      </FormContainer>
    </Container>
  );
};

export default SecurityTab;
